<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';
import { useMobileDevice } from 'dashboard/composables/useMobileDevice';
import wootConstants from 'dashboard/constants/globals';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  SIDEBAR_PAGE_IDS,
  buildNotificationsDefaultSidebarPageKey,
  isSidebarPageReady,
  markSidebarPageReady,
} from 'dashboard/helper/sidebarPagePreload';

import InboxCard from 'dashboard/components-next/Inbox/InboxCard.vue';
import InboxListHeader from './components/InboxListHeader.vue';
import AceleresPortfolioTransferRequestsPanel from './components/AceleresPortfolioTransferRequestsPanel.vue';
import AceleresMyTransferRequestsPanel from './components/AceleresMyTransferRequestsPanel.vue';
import InboxNotificationsEmptyState from './components/InboxNotificationsEmptyState.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { getInboxNotificationState } from './inboxNotificationState';
import InboxTabs from 'dashboard/components-next/Inbox/InboxTabs.vue';
import InboxSortControl from 'dashboard/components-next/Inbox/InboxSortControl.vue';
import EmailInboxList from 'dashboard/components-next/Inbox/Email/EmailInboxList.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useEmailInbox } from 'dashboard/composables/useEmailInbox';
import FollowupNotificationsAPI from 'dashboard/api/aceleres/followupNotifications';
import UnreadCountBadge from 'dashboard/components-next/Inbox/UnreadCountBadge.vue';
import { useInboxUnreadCounts } from 'dashboard/composables/useInboxUnreadCounts';
import { useInboxSidebarBadge } from 'dashboard/composables/useInboxSidebarBadge';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const isMobileDevice = useMobileDevice();

const notificationList = ref(null);
const page = ref(1);
const status = ref('');
const type = ref('');
const sortOrder = ref(wootConstants.INBOX_SORT_BY.NEWEST);
const readFilter = ref('all');
const reminderReadFilter = ref('all');
const notificationSection = ref('reminders');
const searchQuery = ref('');
const isNotificationListLoading = ref(true);
const hasLoadedNotifications = ref(false);
const selectedFollowupNotification = ref(null);
const markingFollowupReadId = ref(null);
const completingFollowupId = ref(null);
const cancellingFollowupId = ref(null);

const INBOX_LIST_WIDTH_KEY = 'acl_inbox_list_width';
const INBOX_LIST_WIDTH_DEFAULT = 497;
const INBOX_LIST_WIDTH_MIN = 320;
const INBOX_LIST_WIDTH_MAX = 900;

const meta = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');
const records = useMapGetter('notifications/getFilteredNotificationsV4');
const inboxById = useMapGetter('inboxes/getInboxById');

const currentDetailId = computed(() => Number(route.params.id));
const hasDetailPanel = computed(
  () =>
    route.name === 'inbox_view_conversation' ||
    route.name === 'inbox_view_email_thread'
);
const isEmailThread = computed(() => route.name === 'inbox_view_email_thread');
const notificationReadTabs = computed(() => [
  { key: 'all', label: t('INBOX.REQUESTS.FILTER_ALL') },
  { key: 'unread', label: t('INBOX.REQUESTS.STATUS_UNREAD') },
  { key: 'read', label: t('INBOX.REQUESTS.STATUS_READ') },
]);
const activeReadFilter = computed(() =>
  notificationSection.value === 'reminders'
    ? reminderReadFilter.value
    : readFilter.value
);

const infiniteLoaderOptions = computed(() => ({
  root: notificationList.value,
  rootMargin: '100px 0px 100px 0px',
}));

// Aceleres 2026-09-15: aba E-mails. O estado vive na query (`?tab=emails`) e
// nao num caminho novo: o menu do celular (public/aceleres-mobile-chatlist-
// bottom.js) acha o cabecalho pela URL `/inbox-view$`.
const { unreadCount: emailUnreadCount } = useEmailInbox();
const { tooltipTitle: unreadTooltipTitle, tooltipLines: unreadTooltipLines } =
  useInboxSidebarBadge(false);
const {
  requestsUnreadCount,
  notificationsUnreadCount,
  setRequestsUnreadCount,
  remindersUnreadCount: followupUnreadCount,
  reminders: followupNotifications,
  remindersLoading: followupLoading,
  remindersError: followupError,
  refreshReminders: fetchFollowupNotifications,
  setReminders,
  startUnreadCountSync,
} = useInboxUnreadCounts();
const activeTab = computed(() =>
  route.name === 'inbox_view_email_thread' || route.query.tab === 'emails'
    ? 'emails'
    : 'notifications'
);
const notificationPanelStates = ref({
  activity: { count: 0, loading: true, error: false },
  mine: { count: 0, loading: true, error: false },
  team: { count: 0, loading: true, error: false },
});
const inboxFilters = computed(() => ({
  page: page.value,
  status: status.value,
  type: type.value,
  sortOrder: sortOrder.value,
}));
const conversationNotifications = computed(() => {
  return records.value(inboxFilters.value).filter(notificationItem => {
    const requestKind = notificationItem.meta?.kind;
    const isApprovalRequest = [
      'aceleres_carteirizacao_request',
      'aceleres_atendimento_request',
      'aceleres_contact_ownership_request',
    ].includes(requestKind);
    const inbox = inboxById.value(notificationItem.primaryActor?.inboxId);

    return !isApprovalRequest && inbox?.channelType !== 'Channel::Email';
  });
});
const isReminderNotification = notificationItem =>
  Boolean(
    notificationItem.snoozedUntil || notificationItem.meta?.lastSnoozedAt
  );
const isFollowupNotification = notificationItem =>
  Boolean(notificationItem?.isFollowupNotification);
const followupDetail = notification =>
  notification?.metadata?.followup_detail || notification?.metadata || {};
const toInboxFollowup = notification => {
  const detail = followupDetail(notification);
  const clientName = detail.client_name || t('INBOX.FOLLOWUPS.CLIENT_UNKNOWN');
  const hasNotification = Boolean(notification.notification_id);
  const scheduledAt = notification.scheduled_at || notification.created_at;

  return {
    id: `followup-task-${notification.task_id || notification.id}`,
    readAt: hasNotification ? notification.read_at : scheduledAt,
    createdAt: scheduledAt,
    lastActivityAt: Math.floor(new Date(scheduledAt).getTime() / 1000),
    pushMessageTitle:
      detail.title || notification.title || t('INBOX.FOLLOWUPS.TITLE'),
    pushMessageBody: notification.body || '',
    primaryActorId: detail.conversation_id,
    primaryActorType: 'Conversation',
    primaryActor: {
      id: detail.conversation_id,
      inboxId: detail.inbox_id,
      contact: { name: clientName, phoneNumber: detail.client_phone },
      meta: {
        sender: { name: clientName, phoneNumber: detail.client_phone },
      },
    },
    isFollowupNotification: true,
    disableNotificationActions: !hasNotification,
    followupNotification: notification,
  };
};
const reminderInboxNotifications = computed(() =>
  followupNotifications.value.map(toInboxFollowup)
);
const sectionActivityNotifications = computed(() => {
  if (notificationSection.value === 'reminders') {
    return reminderInboxNotifications.value;
  }

  if (notificationSection.value === 'conversations') {
    return conversationNotifications.value.filter(
      notificationItem => !isReminderNotification(notificationItem)
    );
  }

  return [];
});
const notifications = computed(() => {
  const normalizedQuery = searchQuery.value.trim().toLowerCase();
  const sourceNotifications = sectionActivityNotifications.value;
  const orderedNotifications =
    notificationSection.value === 'reminders'
      ? [...sourceNotifications].sort((left, right) => {
          const leftTime = new Date(left.createdAt || 0).getTime();
          const rightTime = new Date(right.createdAt || 0).getTime();
          const newestFirst =
            sortOrder.value === wootConstants.INBOX_SORT_BY.NEWEST;
          return newestFirst ? rightTime - leftTime : leftTime - rightTime;
        })
      : sourceNotifications;

  return orderedNotifications.filter(notificationItem => {
    const isRead = Boolean(notificationItem.readAt || notificationItem.read_at);
    if (activeReadFilter.value === 'read' && !isRead) return false;
    if (activeReadFilter.value === 'unread' && isRead) return false;
    if (!normalizedQuery) return true;

    const primaryActor = notificationItem.primaryActor || {};
    const sender = primaryActor.meta?.sender || {};
    const searchableText = [
      notificationItem.pushMessageTitle,
      notificationItem.pushMessageBody,
      primaryActor.displayId,
      primaryActor.id,
      sender.name,
      sender.email,
      sender.phoneNumber,
      primaryActor.contact?.name,
      primaryActor.contact?.phoneNumber,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });
});
const notificationDisplayState = computed(() => {
  if (notificationSection.value === 'requests') {
    return getInboxNotificationState({
      mine: notificationPanelStates.value.mine,
      team: notificationPanelStates.value.team,
    });
  }

  if (notificationSection.value === 'reminders') {
    return getInboxNotificationState({
      activity: {
        count: followupNotifications.value.length,
        loading: followupLoading.value,
        error: followupError.value,
      },
    });
  }

  return getInboxNotificationState({
    activity: {
      count: sectionActivityNotifications.value.length,
      loading: isNotificationListLoading.value,
      error: false,
    },
  });
});
const notificationSections = computed(() => [
  {
    key: 'requests',
    label: t('INBOX.CENTER.NAV.REQUESTS'),
    icon: 'i-lucide-clipboard-check',
  },
  {
    key: 'reminders',
    label: t('INBOX.CENTER.NAV.REMINDERS'),
    icon: 'i-lucide-calendar-clock',
  },
]);
const notificationHeaderTitle = computed(() => {
  if (notificationSection.value === 'requests') {
    return t('INBOX.CENTER.NAV.REQUESTS');
  }

  if (notificationSection.value === 'conversations') {
    return t('INBOX.CENTER.NAV.CONVERSATIONS');
  }

  if (notificationSection.value === 'reminders') {
    return t('INBOX.CENTER.NAV.REMINDERS');
  }

  return t('INBOX.CENTER.ACTIVITY_TITLE');
});
const showRequestPanels = computed(
  () => notificationSection.value === 'requests'
);
const showActivityFeed = computed(
  () => notificationSection.value !== 'requests'
);
const isActivityFeedLoading = computed(() =>
  notificationSection.value === 'reminders'
    ? followupLoading.value
    : isNotificationListLoading.value
);
const showNotificationsLoading = computed(
  () =>
    activeTab.value === 'notifications' &&
    showActivityFeed.value &&
    notificationDisplayState.value === 'loading'
);
const showNotificationsEmptyState = computed(
  () =>
    activeTab.value === 'notifications' &&
    showActivityFeed.value &&
    notificationDisplayState.value === 'empty'
);
const showNotificationsError = computed(
  () =>
    activeTab.value === 'notifications' &&
    notificationSection.value === 'reminders' &&
    followupError.value &&
    followupNotifications.value.length === 0
);
const resetNotificationPanelStates = () => {
  notificationPanelStates.value = {
    activity: {
      count: conversationNotifications.value.length,
      loading: isNotificationListLoading.value,
      error: false,
    },
    mine: { count: 0, loading: true, error: false },
    team: { count: 0, loading: true, error: false },
  };
};
const updateNotificationPanelState = (panel, state) => {
  notificationPanelStates.value = {
    ...notificationPanelStates.value,
    [panel]: state,
  };
};
const onRequestPanelStateChange = state => {
  updateNotificationPanelState('team', state);
  if (!state.loading && !state.error && state.unreadCount !== undefined) {
    setRequestsUnreadCount(state.unreadCount);
  }
};
const updateNotificationListState = () => {
  updateNotificationPanelState('activity', {
    count: conversationNotifications.value.length,
    loading: isNotificationListLoading.value,
    error: false,
  });
};
const updateFollowupNotification = (notification, changes) => {
  const taskId = notification.task_id || notification.id;
  const updated = { ...notification, ...changes };
  setReminders(
    followupNotifications.value.map(item =>
      (item.task_id || item.id) === taskId ? updated : item
    )
  );
  if (
    (selectedFollowupNotification.value?.task_id ||
      selectedFollowupNotification.value?.id) === taskId
  ) {
    selectedFollowupNotification.value = updated;
  }
};
watch(
  activeTab,
  tab => {
    if (tab === 'notifications') {
      fetchFollowupNotifications();
    }
  },
  { immediate: true }
);
watch(
  activeTab,
  tab => {
    if (tab === 'notifications') resetNotificationPanelStates();
  },
  { immediate: true }
);
watch(
  [conversationNotifications, isNotificationListLoading],
  updateNotificationListState,
  { immediate: true }
);

const onTabChange = tab => {
  router.push({
    name: 'inbox_view',
    query: tab === 'emails' ? { tab: 'emails' } : {},
  });
};

const readStoredInboxListWidth = () => {
  try {
    const raw = parseInt(localStorage.getItem(INBOX_LIST_WIDTH_KEY) || '', 10);
    if (
      Number.isFinite(raw) &&
      raw >= INBOX_LIST_WIDTH_MIN &&
      raw <= INBOX_LIST_WIDTH_MAX
    ) {
      return raw;
    }
  } catch {
    // Ignore storage access failures.
  }
  return INBOX_LIST_WIDTH_DEFAULT;
};

const inboxListWidth = ref(readStoredInboxListWidth());
const isInboxListResizing = ref(false);
let inboxListDragStartX = 0;
let inboxListDragStartWidth = 0;

const onInboxListResizeMove = event => {
  // Aceleres 2026-08-25: o delta do rato e visual, a largura e um `width` em
  // CSS (layout). Ver helper/dashboardZoom.js — sem converter, o divisor andava
  // 1.25x mais que o cursor.
  const delta = visualToLayout(event.clientX - inboxListDragStartX);
  const width = Math.min(
    INBOX_LIST_WIDTH_MAX,
    Math.max(INBOX_LIST_WIDTH_MIN, inboxListDragStartWidth + delta)
  );
  inboxListWidth.value = width;
};

const onInboxListResizeEnd = () => {
  isInboxListResizing.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', onInboxListResizeMove);
  window.removeEventListener('mouseup', onInboxListResizeEnd);
  try {
    localStorage.setItem(INBOX_LIST_WIDTH_KEY, String(inboxListWidth.value));
  } catch {
    // Ignore storage access failures.
  }
};

const onInboxListResizeStart = event => {
  isInboxListResizing.value = true;
  inboxListDragStartX = event.clientX;
  inboxListDragStartWidth = inboxListWidth.value;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', onInboxListResizeMove);
  window.addEventListener('mouseup', onInboxListResizeEnd);
};

const onInboxListResizeReset = () => {
  inboxListWidth.value = INBOX_LIST_WIDTH_DEFAULT;
  try {
    localStorage.setItem(
      INBOX_LIST_WIDTH_KEY,
      String(INBOX_LIST_WIDTH_DEFAULT)
    );
  } catch {
    // Ignore storage access failures.
  }
};

const inboxSidebarCacheKey = computed(() =>
  buildNotificationsDefaultSidebarPageKey()
);
const isDefaultInboxSidebarView = computed(
  () =>
    page.value === 1 &&
    !status.value &&
    !type.value &&
    sortOrder.value === wootConstants.INBOX_SORT_BY.NEWEST
);
const showEndOfList = computed(
  () => uiFlags.value.isAllNotificationsLoaded && !uiFlags.value.isFetching
);
const showListEmptyState = computed(
  () => !uiFlags.value.isFetching && !notifications.value.length
);

const stateInbox = inboxId => inboxById.value(inboxId);

const fetchNotifications = async () => {
  page.value = 1;
  isNotificationListLoading.value = true;
  hasLoadedNotifications.value = false;
  store.dispatch('notifications/clear');

  try {
    await store.dispatch('notifications/index', inboxFilters.value);
    hasLoadedNotifications.value = true;
    if (isDefaultInboxSidebarView.value) {
      markSidebarPageReady(
        SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
        inboxSidebarCacheKey.value
      );
    }
  } finally {
    isNotificationListLoading.value = false;
  }
};

const loadMoreNotifications = () => {
  if (uiFlags.value.isAllNotificationsLoaded || uiFlags.value.isFetching) {
    return;
  }

  page.value += 1;
  store.dispatch('notifications/index', {
    ...inboxFilters.value,
    page: page.value,
  });
};

const scrollActiveIntoView = () => {
  const activeEl = notificationList.value?.querySelector('.inbox-card.active');
  activeEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

const redirectToInbox = () => {
  if (route.name === 'inbox_view') return;

  router.replace({ name: 'inbox_view' });
};

const markNotificationAsRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_READ);

  if (isFollowupNotification(notificationItem)) {
    const notification = notificationItem.followupNotification;
    if (!notification.notification_id) return;
    markingFollowupReadId.value = notification.task_id;
    try {
      const result = await FollowupNotificationsAPI.markAsRead(
        notification.notification_id
      );
      if (!result.read_at)
        throw new Error(t('INBOX.FOLLOWUPS.MARK_READ_ERROR'));
      updateFollowupNotification(notification, { read_at: result.read_at });
      useAlert(t('INBOX.ALERTS.MARK_AS_READ'));
    } catch (error) {
      useAlert(error.message || t('INBOX.FOLLOWUPS.MARK_READ_ERROR'));
    } finally {
      markingFollowupReadId.value = null;
    }
    return;
  }

  const { id, primaryActorId, primaryActorType } = notificationItem;

  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });
    useAlert(t('INBOX.ALERTS.MARK_AS_READ'));
    store.dispatch('notifications/unReadCount');
  } catch {
    // Keep the notification available if the update fails.
  }
};

const markNotificationAsUnRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_UNREAD);
  redirectToInbox();

  if (isFollowupNotification(notificationItem)) {
    const notification = notificationItem.followupNotification;
    if (!notification.notification_id) return;
    try {
      await FollowupNotificationsAPI.markAsUnread(notification.notification_id);
      updateFollowupNotification(notification, { read_at: null });
      useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD'));
    } catch (error) {
      useAlert(error.message || t('INBOX.FOLLOWUPS.MARK_READ_ERROR'));
    }
    return;
  }

  try {
    await store.dispatch('notifications/unread', { id: notificationItem.id });
    useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD'));
    store.dispatch('notifications/unReadCount');
  } catch {
    // Keep the notification available if the update fails.
  }
};

const selectedFollowupDetail = computed(() =>
  followupDetail(selectedFollowupNotification.value)
);
const selectedFollowupTaskStatus = computed(
  () =>
    selectedFollowupDetail.value.task_status ||
    selectedFollowupNotification.value?.metadata?.task_status
);
const selectedFollowupCanBeCompleted = computed(
  () =>
    !['completed', 'skipped', 'cancelled'].includes(
      selectedFollowupTaskStatus.value
    )
);
const closeFollowupDetails = () => {
  selectedFollowupNotification.value = null;
};

const confirmFollowup = async () => {
  const notification = selectedFollowupNotification.value;
  const taskId = notification?.task_id || notification?.id;
  if (!notification?.can_confirm || !taskId || completingFollowupId.value)
    return;

  completingFollowupId.value = taskId;
  try {
    const result = await FollowupNotificationsAPI.complete(taskId);
    const readAt =
      result.read_at || notification.read_at || new Date().toISOString();
    const detail = followupDetail(notification);
    updateFollowupNotification(notification, {
      can_confirm: false,
      read_at: readAt,
      metadata: {
        ...notification.metadata,
        task_status: 'completed',
        followup_detail: { ...detail, task_status: 'completed' },
      },
    });
    useAlert(t('INBOX.FOLLOWUPS.COMPLETE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX.FOLLOWUPS.COMPLETE_ERROR'));
  } finally {
    completingFollowupId.value = null;
  }
};

const cancelFollowup = async () => {
  const notification = selectedFollowupNotification.value;
  const taskId = notification?.task_id || notification?.id;
  if (
    !taskId ||
    !selectedFollowupCanBeCompleted.value ||
    cancellingFollowupId.value
  ) {
    return;
  }
  if (!window.confirm(t('INBOX.FOLLOWUPS.REMOVE_CONFIRM'))) return;

  cancellingFollowupId.value = taskId;
  try {
    await FollowupNotificationsAPI.cancel(taskId);
    setReminders(
      followupNotifications.value.filter(
        item => (item.task_id || item.id) !== taskId
      )
    );
    closeFollowupDetails();
    useAlert(t('INBOX.FOLLOWUPS.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX.FOLLOWUPS.REMOVE_ERROR'));
  } finally {
    cancellingFollowupId.value = null;
  }
};

const deleteNotification = async notificationItem => {
  useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
  redirectToInbox();

  if (isFollowupNotification(notificationItem)) {
    const notification = notificationItem.followupNotification;
    if (!notification.notification_id) return;
    try {
      const result = await FollowupNotificationsAPI.delete(
        notification.notification_id
      );
      if (!result.deleted) throw new Error(t('INBOX.FOLLOWUPS.REMOVE_ERROR'));
      const taskId = notification.task_id || notification.id;
      setReminders(
        followupNotifications.value.filter(
          item => (item.task_id || item.id) !== taskId
        )
      );
    } catch (error) {
      useAlert(error.message || t('INBOX.FOLLOWUPS.LOAD_ERROR'));
    }
    return;
  }

  try {
    await store.dispatch('notifications/delete', {
      notification: notificationItem,
      unread_count: meta.value.unreadCount,
      count: meta.value.count,
    });
  } catch {
    // Keep the notification available if deletion fails.
  }
};

const onReadFilterChange = nextReadFilter => {
  if (notificationSection.value === 'reminders') {
    reminderReadFilter.value = nextReadFilter;
    return;
  }

  readFilter.value = nextReadFilter;
  type.value =
    nextReadFilter === 'read' ? wootConstants.INBOX_DISPLAY_BY.READ : '';
  updateUISettings({
    inbox_filter_by: {
      ...uiSettings.value.inbox_filter_by,
      type: type.value,
    },
  });
  fetchNotifications();
};

const selectNotificationSection = section => {
  notificationSection.value = section;
  searchQuery.value = '';
  if (section === 'reminders') reminderReadFilter.value = 'all';
};

const onSortOrderChange = nextSortOrder => {
  sortOrder.value = nextSortOrder;
  updateUISettings({
    inbox_filter_by: {
      ...uiSettings.value.inbox_filter_by,
      sort_by: nextSortOrder,
    },
  });
  fetchNotifications();
};

const setSavedFilter = () => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { status: savedStatus, type: savedType, sort_by: sortBy } = filterBy;
  status.value = savedStatus || '';
  if (savedType === wootConstants.INBOX_DISPLAY_BY.READ) {
    readFilter.value = 'read';
  } else if (savedType) {
    readFilter.value = 'unread';
  } else {
    readFilter.value = 'all';
  }
  type.value = readFilter.value === 'read' ? savedType : '';
  sortOrder.value = sortBy || wootConstants.INBOX_SORT_BY.NEWEST;
  store.dispatch('notifications/setNotificationFilters', inboxFilters.value);
};

const openConversation = async notificationItem => {
  if (isFollowupNotification(notificationItem)) {
    const notification = notificationItem.followupNotification;

    if (!notification?.has_notification) {
      selectedFollowupNotification.value = notification;
      return;
    }

    const conversationId =
      notification?.metadata?.followup_detail?.conversation_id;
    const inboxId = notification?.metadata?.followup_detail?.inbox_id;

    if (notification?.notification_id && !notification.read_at) {
      try {
        const result = await FollowupNotificationsAPI.markAsRead(
          notification.notification_id
        );
        updateFollowupNotification(notification, {
          read_at: result.read_at || new Date().toISOString(),
        });
      } catch (error) {
        useAlert(error.message || t('INBOX.FOLLOWUPS.MARK_READ_ERROR'));
        return;
      }
    }

    if (!conversationId) {
      selectedFollowupNotification.value = notification;
      return;
    }

    if (inboxId) {
      router.push({
        name: 'conversation_through_inbox',
        params: {
          inbox_id: inboxId,
          conversation_id: conversationId,
        },
      });
    } else {
      router.push({
        name: 'inbox_conversation',
        params: {
          accountId: route.params.accountId,
          conversation_id: conversationId,
        },
      });
    }
    return;
  }

  const {
    id,
    primaryActorId,
    primaryActorType,
    primaryActor: { inboxId, id: conversationId },
    notificationType,
  } = notificationItem;

  if (route.params.id === String(conversationId)) return;

  useTrack(INBOX_EVENTS.OPEN_CONVERSATION_VIA_INBOX, { notificationType });

  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });
    store.dispatch('notifications/unReadCount');
    router.push(
      inboxId
        ? {
            name: 'conversation_through_inbox',
            params: {
              inbox_id: inboxId,
              conversation_id: conversationId,
            },
          }
        : {
            name: 'inbox_conversation',
            params: {
              accountId: route.params.accountId,
              conversation_id: conversationId,
            },
          }
    );
  } catch {
    // Keep the notification available if opening the conversation fails.
  }
};

const selectedFollowupNotificationStatus = computed(() => {
  const notification = selectedFollowupNotification.value;
  if (notification?.notification_dismissed) {
    return t('INBOX.FOLLOWUPS.STATUS_DISMISSED');
  }
  if (notification?.notification_pending) {
    return t('INBOX.FOLLOWUPS.STATUS_AWAITING');
  }
  if (notification?.notification_not_created) {
    return t('INBOX.FOLLOWUPS.STATUS_NOT_CREATED');
  }
  if (!notification?.notification_id) {
    return t('INBOX.FOLLOWUPS.STATUS_SCHEDULED');
  }

  return notification.read_at
    ? t('INBOX.FOLLOWUPS.STATUS_READ')
    : t('INBOX.FOLLOWUPS.STATUS_UNREAD');
});
const taskStatusLabel = statusValue => {
  const labels = {
    pending: t('INBOX.FOLLOWUPS.TASK_PENDING'),
    pending_acceptance: t('INBOX.FOLLOWUPS.TASK_PENDING'),
    in_progress: t('INBOX.FOLLOWUPS.TASK_IN_PROGRESS'),
    andamento: t('INBOX.FOLLOWUPS.TASK_IN_PROGRESS'),
    completed: t('INBOX.FOLLOWUPS.TASK_COMPLETED'),
    skipped: t('INBOX.FOLLOWUPS.TASK_SKIPPED'),
    cancelled: t('INBOX.FOLLOWUPS.TASK_CANCELLED'),
  };

  return labels[statusValue] || t('INBOX.FOLLOWUPS.TASK_STATUS_UNKNOWN');
};
const formatFollowupDate = value => {
  if (!value) return t('INBOX.FOLLOWUPS.NOT_INFORMED');
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;

  const [, year, month, day] = match;
  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(Number(year), Number(month) - 1, Number(day))
  );
};
const viewFollowupConversation = async () => {
  const conversationId = selectedFollowupDetail.value.conversation_id;
  if (!conversationId) {
    useAlert(t('INBOX.FOLLOWUPS.CONVERSATION_UNAVAILABLE'));
    return;
  }

  closeFollowupDetails();
  await router.push({
    name: 'inbox_conversation',
    params: {
      accountId:
        selectedFollowupDetail.value.chatwoot_account_id ||
        route.params.accountId,
      conversation_id: conversationId,
    },
  });
};

watch(
  inboxFilters,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      store.dispatch('notifications/updateNotificationFilters', newValue);
    }
  },
  { deep: true }
);

watch(currentDetailId, () => nextTick(scrollActiveIntoView));

onMounted(() => {
  startUnreadCountSync();
  scrollActiveIntoView();
  setSavedFilter();

  if (
    isDefaultInboxSidebarView.value &&
    isSidebarPageReady(
      SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
      inboxSidebarCacheKey.value
    )
  ) {
    hasLoadedNotifications.value = true;
    isNotificationListLoading.value = false;
    return;
  }

  fetchNotifications();
});

onUnmounted(() => {
  window.removeEventListener('mousemove', onInboxListResizeMove);
  window.removeEventListener('mouseup', onInboxListResizeEnd);
});
</script>

<template>
  <section class="painel-atendimento flex w-full h-full min-w-0 bg-n-surface-1">
    <div
      class="acl-inbox-list-resizable relative flex h-full w-full flex-col flex-shrink-0 bg-n-surface-1 ltr:border-r rtl:border-l border-n-weak"
      :class="
        isMobileDevice && currentDetailId && !isEmailThread ? 'hidden' : 'flex'
      "
      :style="{
        '--acl-inbox-list-width':
          hasDetailPanel && !isEmailThread ? `${inboxListWidth}px` : '100%',
        '--acl-inbox-list-max-width':
          hasDetailPanel && !isEmailThread ? '900px' : 'none',
      }"
    >
      <InboxListHeader />
      <InboxTabs
        :model-value="activeTab"
        :notifications-count="notificationsUnreadCount"
        :emails-count="emailUnreadCount"
        :unread-tooltip-title="unreadTooltipTitle"
        :unread-tooltip-lines="unreadTooltipLines"
        @update:model-value="onTabChange"
      />
      <EmailInboxList v-if="activeTab === 'emails'" />
      <div
        v-if="activeTab === 'notifications'"
        class="flex min-h-0 flex-1 flex-col md:flex-row"
      >
        <nav
          class="flex shrink-0 gap-2 overflow-x-auto border-b border-n-weak bg-n-surface-1 px-3 py-3 md:hidden"
          :aria-label="t('INBOX.CENTER.NAV.LABEL')"
        >
          <button
            v-for="section in notificationSections"
            :key="section.key"
            type="button"
            class="flex min-h-10 min-w-max items-center gap-2 rounded-xl border px-3 text-left text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#15803D]"
            :class="
              notificationSection === section.key
                ? 'border-transparent bg-[#E8F6EC] font-bold text-[#14532D]'
                : 'border-n-weak bg-n-surface-1 text-n-slate-10 hover:bg-n-alpha-2'
            "
            :aria-current="
              notificationSection === section.key ? 'page' : undefined
            "
            @click="selectNotificationSection(section.key)"
          >
            <span
              class="size-4 flex-shrink-0"
              :class="section.icon"
              aria-hidden="true"
            />
            <span>{{ section.label }}</span>
            <UnreadCountBadge
              :count="
                section.key === 'requests'
                  ? requestsUnreadCount
                  : followupUnreadCount
              "
            />
          </button>
        </nav>

        <aside
          class="hidden w-56 shrink-0 flex-col border-r border-n-weak bg-n-surface-1 px-3 py-5 md:flex lg:w-64"
        >
          <div class="px-2">
            <p
              class="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-teal-11"
            >
              {{ t('INBOX.CENTER.SELECT_TITLE') }}
            </p>
            <p class="mt-1 text-xs leading-relaxed text-n-slate-10">
              {{ t('INBOX.CENTER.SELECT_DESCRIPTION') }}
            </p>
          </div>
          <nav
            class="mt-6 flex flex-col gap-1.5"
            :aria-label="t('INBOX.CENTER.NAV.LABEL')"
          >
            <button
              v-for="section in notificationSections"
              :key="section.key"
              type="button"
              class="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#15803D]"
              :class="
                notificationSection === section.key
                  ? 'bg-[#E8F6EC] font-bold text-[#14532D]'
                  : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
              :aria-current="
                notificationSection === section.key ? 'page' : undefined
              "
              @click="selectNotificationSection(section.key)"
            >
              <span
                class="size-5 flex-shrink-0"
                :class="section.icon"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 truncate">{{ section.label }}</span>
              <UnreadCountBadge
                :count="
                  section.key === 'requests'
                    ? requestsUnreadCount
                    : followupUnreadCount
                "
              />
            </button>
          </nav>
          <div
            v-if="showActivityFeed"
            class="mt-auto rounded-2xl border border-n-weak bg-n-surface-2 p-3"
          >
            <div class="flex items-center gap-2 text-n-teal-11">
              <span class="i-lucide-bell-ring size-4" aria-hidden="true" />
              <span class="text-xs font-semibold">{{
                t('INBOX.CENTER.ACTIVITY_TITLE')
              }}</span>
            </div>
            <p class="mt-1.5 text-xs leading-relaxed text-n-slate-10">
              {{ t('INBOX.CENTER.ACTIVITY_DESCRIPTION') }}
            </p>
          </div>
        </aside>

        <main class="flex min-h-0 min-w-0 flex-1 flex-col bg-n-surface-2">
          <header
            class="shrink-0 border-b border-n-weak bg-n-surface-1 px-4 py-3 md:px-6 md:py-4"
          >
            <div class="flex items-center justify-between gap-3">
              <h1
                class="truncate text-lg font-semibold text-n-slate-12 md:text-xl"
              >
                {{ notificationHeaderTitle }}
              </h1>
              <span
                v-if="
                  notificationSection === 'requests' ||
                  notificationSection === 'reminders'
                "
                class="shrink-0 text-xs font-medium text-n-slate-10"
              >
                {{
                  t('INBOX.BADGES.UNREAD_ITEMS', {
                    count:
                      notificationSection === 'requests'
                        ? requestsUnreadCount
                        : followupUnreadCount,
                  })
                }}
              </span>
            </div>
          </header>

          <section
            v-if="showActivityFeed"
            class="shrink-0 border-b border-n-weak bg-n-surface-1 px-3 py-3 md:px-5 md:py-4"
          >
            <div class="hidden items-center gap-3 md:flex">
              <label
                class="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3.5 text-n-slate-10 transition-colors focus-within:border-n-teal-8"
              >
                <span
                  class="i-lucide-search size-4 shrink-0"
                  aria-hidden="true"
                />
                <input
                  v-model="searchQuery"
                  type="text"
                  inputmode="search"
                  autocomplete="off"
                  class="min-w-0 flex-1 appearance-none !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9"
                  :placeholder="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                  :aria-label="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="inline-flex size-7 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2"
                  :aria-label="t('INBOX.EMAIL.CLEAR_SEARCH')"
                  @click="searchQuery = ''"
                >
                  <span class="i-lucide-x size-4" aria-hidden="true" />
                </button>
              </label>
              <div
                class="flex shrink-0 items-center gap-1 rounded-xl bg-n-surface-2 p-1"
                role="tablist"
                :aria-label="t('INBOX.DISPLAY_MENU.DISPLAY')"
              >
                <button
                  v-for="tab in notificationReadTabs"
                  :key="`desktop-${tab.key}`"
                  type="button"
                  role="tab"
                  class="min-h-9 rounded-lg px-3 text-xs font-semibold transition-colors"
                  :class="
                    activeReadFilter === tab.key
                      ? 'bg-n-surface-1 text-n-teal-11 shadow-sm'
                      : 'text-n-slate-10 hover:text-n-slate-12'
                  "
                  :aria-selected="activeReadFilter === tab.key"
                  @click="onReadFilterChange(tab.key)"
                >
                  {{ tab.label }}
                  <span
                    v-if="
                      notificationSection === 'reminders' &&
                      tab.key === 'unread'
                    "
                    class="ml-1"
                  >
                    <UnreadCountBadge :count="followupUnreadCount" />
                  </span>
                </button>
              </div>
              <InboxSortControl
                class="[--acl-inbox-sort-active-background:#d9fdd3] [--acl-inbox-sort-active-color:#15603e]"
                :sort-order="sortOrder"
                @update:sort-order="onSortOrderChange"
              />
            </div>

            <div class="flex flex-col gap-2 md:hidden">
              <label
                class="flex h-10 w-full items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-10 transition-colors focus-within:border-n-teal-8"
              >
                <span
                  class="i-lucide-search size-4 shrink-0"
                  aria-hidden="true"
                />
                <input
                  v-model="searchQuery"
                  type="text"
                  inputmode="search"
                  autocomplete="off"
                  class="min-w-0 flex-1 appearance-none !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9"
                  :placeholder="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                  :aria-label="t('INBOX.EMAIL.SEARCH_PLACEHOLDER')"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="inline-flex size-8 items-center justify-center rounded-full text-n-slate-10 hover:bg-n-alpha-2"
                  :aria-label="t('INBOX.EMAIL.CLEAR_SEARCH')"
                  @click="searchQuery = ''"
                >
                  <span class="i-lucide-x size-4" aria-hidden="true" />
                </button>
              </label>
              <div class="flex items-center gap-2">
                <div
                  class="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto rounded-xl bg-n-surface-2 p-1"
                  role="tablist"
                  :aria-label="t('INBOX.DISPLAY_MENU.DISPLAY')"
                >
                  <button
                    v-for="tab in notificationReadTabs"
                    :key="`mobile-${tab.key}`"
                    type="button"
                    role="tab"
                    class="min-h-9 shrink-0 rounded-lg px-3 text-xs font-semibold transition-colors"
                    :class="
                      activeReadFilter === tab.key
                        ? 'bg-n-surface-1 text-n-teal-11 shadow-sm'
                        : 'text-n-slate-10'
                    "
                    :aria-selected="activeReadFilter === tab.key"
                    @click="onReadFilterChange(tab.key)"
                  >
                    {{ tab.label }}
                    <span
                      v-if="
                        notificationSection === 'reminders' &&
                        tab.key === 'unread'
                      "
                      class="ml-1"
                    >
                      <UnreadCountBadge :count="followupUnreadCount" />
                    </span>
                  </button>
                </div>
                <InboxSortControl
                  class="[--acl-inbox-sort-active-background:#d9fdd3] [--acl-inbox-sort-active-color:#15603e]"
                  :sort-order="sortOrder"
                  @update:sort-order="onSortOrderChange"
                />
              </div>
            </div>
          </section>
          <div
            v-if="
              showActivityFeed &&
              notificationDisplayState === 'content' &&
              (sectionActivityNotifications.length || isActivityFeedLoading)
            "
            ref="notificationList"
            class="acl-inbox-notifications-list flex min-h-0 w-full flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto bg-n-surface-2 px-2 py-2 md:gap-3 md:px-4 md:py-4"
          >
            <InboxCard
              v-for="notificationItem in notifications"
              :key="notificationItem.id"
              :inbox-item="notificationItem"
              :state-inbox="stateInbox(notificationItem.primaryActor?.inboxId)"
              class="inbox-card"
              :class="[
                currentDetailId === notificationItem.primaryActor?.id
                  ? 'ring-2 ring-inset ring-n-teal-8 active'
                  : '',
              ]"
              @mark-notification-as-read="markNotificationAsRead"
              @mark-notification-as-un-read="markNotificationAsUnRead"
              @delete-notification="deleteNotification"
              @click="openConversation(notificationItem)"
            />
            <div
              v-if="uiFlags.isFetching || followupLoading"
              class="my-4 flex justify-center"
            >
              <Spinner class="text-n-brand" />
            </div>
            <p
              v-if="hasLoadedNotifications && showListEmptyState"
              class="rounded-xl border border-dashed border-n-weak bg-n-surface-1 p-6 text-center text-sm font-medium text-n-slate-10"
            >
              {{ $t('INBOX.LIST.NO_NOTIFICATIONS') }}
            </p>
            <IntersectionObserver
              v-if="
                notificationSection !== 'reminders' &&
                !showEndOfList &&
                !uiFlags.isFetching
              "
              :options="infiniteLoaderOptions"
              @observed="loadMoreNotifications"
            />
          </div>
          <div
            v-if="showNotificationsLoading"
            class="flex min-h-40 flex-1 flex-col items-center justify-center gap-2 text-n-slate-9"
            role="status"
            data-testid="inbox-notifications-loading"
          >
            <Spinner class="text-n-brand" />
            <span class="text-sm">{{ $t('INBOX.CENTER.LOADING') }}</span>
          </div>
          <div
            v-else-if="showNotificationsError"
            class="flex min-h-40 flex-1 flex-col items-center justify-center gap-3 px-6 text-center"
          >
            <p class="text-sm text-n-slate-10">
              {{ t('INBOX.FOLLOWUPS.LOAD_ERROR') }}
            </p>
            <button
              type="button"
              class="rounded-lg bg-n-teal-9 px-4 py-2 text-sm font-semibold text-white hover:bg-n-teal-10"
              @click="fetchFollowupNotifications"
            >
              {{ t('INBOX.FOLLOWUPS.RETRY') }}
            </button>
          </div>
          <InboxNotificationsEmptyState
            v-else-if="showNotificationsEmptyState"
          />
          <div
            v-show="showRequestPanels"
            class="shrink-0 bg-n-surface-1 px-3 pt-3 md:px-5 md:pt-4"
          >
            <AceleresMyTransferRequestsPanel
              @state-change="updateNotificationPanelState('mine', $event)"
            />
            <AceleresPortfolioTransferRequestsPanel
              @state-change="onRequestPanelStateChange"
            />
          </div>
          <div
            v-if="selectedFollowupNotification"
            class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
            role="presentation"
            @click.self="closeFollowupDetails"
          >
            <section
              class="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-n-surface-1 p-5 shadow-xl md:p-6"
              role="dialog"
              aria-modal="true"
              :aria-label="t('INBOX.FOLLOWUPS.DETAILS_TITLE')"
            >
              <header class="flex items-start justify-between gap-4">
                <div class="flex items-center gap-2">
                  <span
                    class="i-lucide-calendar-check size-5 text-n-teal-10"
                    aria-hidden="true"
                  />
                  <h2 class="text-lg font-semibold text-n-slate-12">
                    {{ t('INBOX.FOLLOWUPS.DETAILS_TITLE') }}
                  </h2>
                </div>
                <button
                  type="button"
                  class="flex size-8 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2"
                  :aria-label="t('INBOX.FOLLOWUPS.CLOSE')"
                  @click="closeFollowupDetails"
                >
                  <span class="i-lucide-x size-4" aria-hidden="true" />
                </button>
              </header>
              <h3 class="mt-5 text-base font-semibold text-n-slate-12">
                {{
                  selectedFollowupDetail.title ||
                  selectedFollowupNotification.title
                }}
              </h3>
              <dl
                class="mt-4 grid min-h-0 flex-1 gap-3 overflow-y-auto text-sm"
              >
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.CLIENT') }}
                  </dt>
                  <dd class="mt-0.5 text-n-slate-12">
                    {{
                      selectedFollowupDetail.client_name ||
                      t('INBOX.FOLLOWUPS.CLIENT_UNKNOWN')
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.PHONE') }}
                  </dt>
                  <dd class="mt-0.5 text-n-slate-12">
                    {{
                      selectedFollowupDetail.client_phone ||
                      t('INBOX.FOLLOWUPS.NOT_INFORMED')
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.SCHEDULED_AT') }}
                  </dt>
                  <dd class="mt-0.5 text-n-slate-12">
                    {{
                      formatFollowupDate(selectedFollowupDetail.scheduled_date)
                    }}
                    {{ selectedFollowupDetail.scheduled_time || '' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.TASK_STATUS') }}
                  </dt>
                  <dd class="mt-0.5 text-n-slate-12">
                    {{ taskStatusLabel(selectedFollowupTaskStatus) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.NOTES') }}
                  </dt>
                  <dd class="mt-0.5 whitespace-pre-wrap text-n-slate-12">
                    {{
                      selectedFollowupDetail.notes ||
                      t('INBOX.FOLLOWUPS.NO_NOTES')
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-n-slate-9">
                    {{ t('INBOX.FOLLOWUPS.NOTIFICATION_STATUS') }}
                  </dt>
                  <dd class="mt-0.5 text-n-slate-12">
                    {{ selectedFollowupNotificationStatus }}
                  </dd>
                </div>
              </dl>
              <footer class="mt-6 flex shrink-0 flex-wrap justify-end gap-2">
                <button
                  v-if="selectedFollowupCanBeCompleted"
                  type="button"
                  class="min-h-10 rounded-lg border border-n-ruby-8 px-4 text-sm font-semibold text-n-ruby-9 hover:bg-n-ruby-1 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="
                    cancellingFollowupId ===
                    selectedFollowupNotification.task_id
                  "
                  @click="cancelFollowup"
                >
                  {{
                    cancellingFollowupId ===
                    selectedFollowupNotification.task_id
                      ? t('INBOX.FOLLOWUPS.REMOVING')
                      : t('INBOX.FOLLOWUPS.REMOVE')
                  }}
                </button>
                <button
                  type="button"
                  class="min-h-10 rounded-lg bg-emerald-500 px-4 text-sm font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="
                    !selectedFollowupCanBeCompleted ||
                    !selectedFollowupNotification.can_confirm ||
                    completingFollowupId ===
                      selectedFollowupNotification.task_id
                  "
                  @click="confirmFollowup"
                >
                  {{
                    completingFollowupId ===
                    selectedFollowupNotification.task_id
                      ? t('INBOX.FOLLOWUPS.CONFIRMING')
                      : t('INBOX.FOLLOWUPS.CONFIRM')
                  }}
                </button>
                <button
                  type="button"
                  class="min-h-10 rounded-lg bg-n-teal-9 px-4 text-sm font-semibold text-white hover:bg-n-teal-10"
                  @click="viewFollowupConversation"
                >
                  {{ t('INBOX.FOLLOWUPS.VIEW_CONVERSATION') }}
                </button>
              </footer>
            </section>
          </div>
        </main>
      </div>
      <div
        v-if="hasDetailPanel && !isEmailThread"
        class="acl-inbox-list-resize-handle"
        :class="{ 'acl-inbox-list-resize-handle--active': isInboxListResizing }"
        @mousedown.prevent="onInboxListResizeStart"
        @dblclick="onInboxListResizeReset"
      />
    </div>
    <router-view
      v-if="hasDetailPanel && !isEmailThread"
      class="min-h-0 min-w-0 flex-1"
    />
    <CmdBarConversationSnooze />
  </section>
</template>

<style scoped>
.acl-inbox-list-resizable {
  min-height: 0;
  overflow: hidden;
}

.acl-inbox-list-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.12s;
}

.acl-inbox-list-resize-handle:hover,
.acl-inbox-list-resize-handle--active {
  background: rgba(100, 116, 139, 0.35);
}

@media (min-width: 1280px) {
  .acl-inbox-list-resizable {
    width: var(--acl-inbox-list-width, 497px);
    min-width: 320px;
    max-width: var(--acl-inbox-list-max-width, 900px);
  }
}
</style>
